<script lang="ts" setup>
  import { useI18n } from "vue-i18n";
  import { watch, computed } from "vue";
  import { useSessionStore } from "@/stores/session";
  import SimpleModal from "@/components/modals/SimpleModal.vue";
  import * as amplitude from "@amplitude/analytics-browser";

  const { t } = useI18n();

  const props = defineProps<{
    modelValue: boolean;
  }>();
  const emit = defineEmits<{
    'update:modelValue': [value: boolean];
    'close': [];
  }>();

  const value = computed<boolean>({
    get() {
      return props.modelValue;
    },
    set(value) {
      emit('update:modelValue', value);
    },
  });

  watch(value, (value) => {
    if (value) {
      amplitude.track('Sweepstake Tickets Viewed');
    }
  });

  const sessionStore = useSessionStore();
  const activeEntries = computed(() => sessionStore.lotteryNumbers.length);

</script>

<template>
  <SimpleModal
    v-model="value"
    @close="$emit('close')"
    compact
    maxWidth="600"
  >
    <div class="content">
      <div class="t-title-2 t-bold title">
        {{ $t('modal.sweepstakes.title') }}
      </div>
      <div class="t-body-1 subtitle">
        {{ $t('modal.sweepstakes.subtitle', { entries: activeEntries }) }}
      </div>
      <div class="tickets">
        <span
          v-for="ticket in sessionStore.lotteryNumbers"
          :key="`ticket-${ticket}`"
        >{{ ticket }}</span>
      </div>
    </div>
  </SimpleModal>
</template>

<style lang="scss" scoped>
  .content {
    min-width: 400px;
    padding: 0 1rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    text-align: center;

    .title {
      line-height: 130%;
    }

    .subtitle {
      color: rgb(var(--v-theme-neutral-400));
    }

    .tickets {
      margin-top: 1rem;
      max-height: 500px;
      overflow-y: auto;
      overflow-x: hidden;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;

      & > span {
        white-space: nowrap;
        width: calc(25% - 1rem);
        font-weight: getFontWeight('bold');
      }
    }
  }

  @media (max-width: breakpoint(md)) {
    .content {
      .tickets {
        & > span {
          width: calc(33% - 1rem);
        }
      }
    }
  }

  @media (max-width: breakpoint(sm) - 1px) {
    .content {
      padding-top: 0;

      .tickets {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 1rem;
        & > span {
          width: 100%;
          text-align: center;
          white-space: normal;
        }
      }
    }
  }

</style>
